import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, Overline } from "./Reveal";
import { ArrowRight, CheckCircle2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPERIENCE = ["Trainee / Graduate", "1–3 years", "3–7 years", "7+ years"];
const STRATEGY = ["Discretionary Macro", "Systematic / Quant", "Volatility", "Equities", "FX / Rates", "Other"];
const TRACK = ["Discretionary Desk", "Quantitative Desk", "Trainee Track"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute">{label}</span>
    <div className="mt-3">{children}</div>
  </label>
);

const inputCls = "w-full bg-transparent border-b border-line py-3 text-bone placeholder:text-mute/50 focus:border-brass focus:outline-none transition-colors";

const Pills = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {options.map((o) => (
      <button
        key={o}
        type="button"
        onClick={() => onChange(o)}
        className={`font-mono text-[11px] uppercase tracking-[0.12em] px-4 py-2.5 border transition-colors ${
          value === o ? "bg-brass text-ink border-brass" : "border-line text-mute hover:border-brass hover:text-bone"
        }`}
      >
        {o}
      </button>
    ))}
  </div>
);

export const ApplyForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", experience: "", strategy: "", capital_track: "", position: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const handler = (e) => {
      const v = e.detail || {};
      setForm((f) => ({
        ...f,
        position: v.title || "",
        capital_track: v.capital_track || f.capital_track,
      }));
      setDone(false);
      document.querySelector("#apply")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("apply-role", handler);
    return () => window.removeEventListener("apply-role", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.experience || !form.strategy || !form.capital_track) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
      toast.success("Application received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="apply" data-testid="apply" className="border-t border-line bg-surface">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-36 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-4">
          <Reveal><Overline>Apply to Trade</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-6 leading-[1.05]">
              Put your edge to work.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-mute leading-relaxed mt-8">
              Tell us who you are and how you trade. Every application is read by a member of the desk — not a machine.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-8">
          {done ? (
            <Reveal>
              <div data-testid="apply-success" className="border border-brass/40 bg-ink p-10 sm:p-14 text-center">
                <CheckCircle2 className="mx-auto text-brass" size={44} />
                <h3 className="font-serif text-3xl text-bone mt-6">Application received.</h3>
                <p className="text-mute mt-3 max-w-md mx-auto leading-relaxed">
                  Thank you, {form.full_name.split(" ")[0]}. A member of our desk will review your submission and reach out within two weeks.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form data-testid="apply-form" onSubmit={submit} className="border border-line bg-ink p-6 sm:p-10 space-y-10">
                {form.position && (
                  <div data-testid="apply-position-banner" className="flex items-center justify-between gap-4 border border-brass/40 bg-brass/5 px-5 py-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brass">Applying for</div>
                      <div className="font-serif text-xl sm:text-2xl text-bone mt-1">{form.position}</div>
                    </div>
                    <button type="button" data-testid="apply-position-clear" onClick={() => set("position")("")} className="text-mute hover:text-bone transition-colors shrink-0" aria-label="Clear selected role">
                      <X size={18} />
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  <Field label="Full name *">
                    <input data-testid="apply-name" className={inputCls} placeholder="Jane Trader" value={form.full_name} onChange={(e) => set("full_name")(e.target.value)} />
                  </Field>
                  <Field label="Email *">
                    <input data-testid="apply-email" type="email" className={inputCls} placeholder="you@email.com" value={form.email} onChange={(e) => set("email")(e.target.value)} />
                  </Field>
                </div>

                <Field label="Trading experience *">
                  <Pills options={EXPERIENCE} value={form.experience} onChange={set("experience")} testid="apply-experience" />
                </Field>

                <Field label="Primary strategy *">
                  <Pills options={STRATEGY} value={form.strategy} onChange={set("strategy")} testid="apply-strategy" />
                </Field>

                <Field label="Desired capital track *">
                  <Pills options={TRACK} value={form.capital_track} onChange={set("capital_track")} testid="apply-track" />
                </Field>

                <Field label="Tell us about your edge">
                  <textarea data-testid="apply-message" rows={4} className={`${inputCls} resize-none`} placeholder="Describe your approach, track record, or what sets you apart." value={form.message} onChange={(e) => set("message")(e.target.value)} />
                </Field>

                <button
                  data-testid="apply-submit"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] bg-brass text-ink px-8 py-4 hover:bg-bone transition-colors disabled:opacity-50"
                >
                  {submitting ? "Submitting…" : "Submit application"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
